import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productsByCategory, allProducts, type Product } from '@/mocks/products';
import { useNavigate } from 'react-router-dom';

interface ProductCardProps {
  product: Product;
}

function ProductCard({ product }: ProductCardProps) {
  return (
    <a
      href={product.url}
      target="_blank"
      rel="noopener nofollow noreferrer"
      className="group flex-shrink-0 w-[200px] md:w-[240px] rounded-xl bg-white border border-background-200 hover:border-primary-300 overflow-hidden transition-all duration-200 hover:translate-y-[-2px] hover:shadow-md cursor-pointer"
    >
      <div className="aspect-square overflow-hidden bg-background-100">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20geometric%20pattern%20soft%20pastel%20colors%20clean%20modern%20background%20texture&width=300&height=300&seq=product-fallback&orientation=squarish';
          }}
        />
      </div>
      <div className="p-3">
        <h3 className="text-xs font-semibold text-foreground-800 leading-tight line-clamp-2 mb-1.5 group-hover:text-primary-600 transition-colors">
          {product.name}
        </h3>
        <p className="text-xs font-bold text-primary-600">
          {product.price}
        </p>
      </div>
    </a>
  );
}

interface ProductCarouselProps {
  category: string;
  products: Product[];
}

function ProductCarousel({ category, products }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 260;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
    setTimeout(checkScroll, 300);
  };

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4 px-4 md:px-0">
        <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-800">
          {category}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="w-8 h-8 rounded-full bg-white border border-background-200 flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-sm" />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="w-8 h-8 rounded-full bg-white border border-background-200 flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 overflow-x-auto pb-4 px-4 md:px-0 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
}

export default function ProductSection() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';

  return (
    <section className="section-padding bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('products.title', 'Featured Products')}
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto mb-6">
            {t('products.subtitle', 'Browse curated products from our main spreadsheet. Click any item to view details and purchase on our partner site.')}
          </p>
          <button
            onClick={() => navigate(`/${lang}/products`)}
            className="btn-primary text-sm px-6 py-3"
          >
            <i className="ri-grid-line mr-2" />
            {t('products.viewAll', 'View All Products')}
          </button>
        </div>

        {/* Featured: first 2 categories */}
        <ProductCarousel category="Shoes" products={productsByCategory['Shoes']} />
        <ProductCarousel category="Hoodies/Sweaters" products={productsByCategory['Hoodies/Sweaters']} />
        <ProductCarousel category="T-Shirts" products={productsByCategory['T-Shirts']} />
        <ProductCarousel category="Jackets" products={productsByCategory['Jackets']} />
        <ProductCarousel category="Accessories" products={productsByCategory['Accessories']} />
        <ProductCarousel category="Bags" products={productsByCategory['Bags']} />
      </div>
    </section>
  );
}